import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Loader2 } from 'lucide-react'
import { fetchCardmarketNonSinglesList, fetchCardmarketSinglesList, type CardmarketNonSinglesListResponse, type CardmarketSinglesListResponse } from '@/services/cardService'
import { CardMarketNonSinglesTable } from './CardMarketNonSinglesTable'
import { CardMarketSinglesTable } from './CardMarketSinglesTable'

const EXPANSION_PRODUCTS_PAGE_SIZE = 100

export type CardmarketExpansionProductsDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  expansionId: number
  expansionLabel: string
}

export function CardmarketExpansionProductsDialog({ open, onOpenChange, expansionId, expansionLabel }: CardmarketExpansionProductsDialogProps) {
  const [kind, setKind] = useState<'singles' | 'nonsingles'>('singles')
  const [offset, setOffset] = useState(0)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [singlesData, setSinglesData] = useState<CardmarketSinglesListResponse | null>(null)
  const [nonSinglesData, setNonSinglesData] = useState<CardmarketNonSinglesListResponse | null>(null)

  useEffect(() => {
    if (!open) return
    setKind('singles')
    setOffset(0)
    setErr(null)
    setSinglesData(null)
    setNonSinglesData(null)
  }, [open, expansionId])

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    setErr(null)
    void (async () => {
      try {
        if (kind === 'singles') {
          const json = await fetchCardmarketSinglesList({
            idExpansion: expansionId,
            limit: EXPANSION_PRODUCTS_PAGE_SIZE,
            offset,
          })
          if (!cancelled) {
            setSinglesData(json)
            setNonSinglesData(null)
          }
        } else {
          const json = await fetchCardmarketNonSinglesList({
            idExpansion: expansionId,
            limit: EXPANSION_PRODUCTS_PAGE_SIZE,
            offset,
          })
          if (!cancelled) {
            setNonSinglesData(json)
            setSinglesData(null)
          }
        }
      } catch (e) {
        if (!cancelled) {
          setErr(e instanceof Error ? e.message : 'Failed to load products')
          setSinglesData(null)
          setNonSinglesData(null)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, expansionId, kind, offset])

  const meta = kind === 'singles' ? singlesData : nonSinglesData
  const total = meta?.total ?? 0
  const canPrev = offset > 0
  const canNext = offset + EXPANSION_PRODUCTS_PAGE_SIZE < total

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton className="flex max-h-[min(90vh,900px)] max-w-[calc(100%-2rem)] flex-col gap-3 overflow-hidden p-4 sm:max-w-5xl">
        <DialogHeader className="shrink-0 space-y-1 text-left">
          <DialogTitle className="pr-8">Cardmarket products · expansion {expansionId}</DialogTitle>
          <DialogDescription className="line-clamp-2">{expansionLabel.trim() ? expansionLabel : 'Cached singles and non-singles for this expansion id.'}</DialogDescription>
        </DialogHeader>

        <div className="inline-flex shrink-0 flex-wrap gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1">
          <Button
            type="button"
            variant={kind === 'singles' ? 'default' : 'ghost'}
            size="sm"
            className="rounded-md"
            onClick={() => {
              setKind('singles')
              setOffset(0)
            }}
          >
            Singles
          </Button>
          <Button
            type="button"
            variant={kind === 'nonsingles' ? 'default' : 'ghost'}
            size="sm"
            className="rounded-md"
            onClick={() => {
              setKind('nonsingles')
              setOffset(0)
            }}
          >
            Non-singles
          </Button>
        </div>

        {meta && !err && (
          <p className="shrink-0 text-sm text-muted-foreground">
            List version {meta.version} · updated {meta.createdAt} · showing {meta.items.length} of {meta.total} in this expansion
          </p>
        )}

        {err && <p className="shrink-0 text-sm text-destructive">{err}</p>}

        <div className="min-h-0 flex-1 overflow-y-auto rounded-md border border-transparent">
          {loading && (
            <div className="flex items-center gap-2 p-4 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading…
            </div>
          )}
          {!loading && kind === 'singles' && singlesData && <CardMarketSinglesTable items={singlesData.items} />}
          {!loading && kind === 'nonsingles' && nonSinglesData && <CardMarketNonSinglesTable items={nonSinglesData.items} />}
        </div>

        {meta && total > EXPANSION_PRODUCTS_PAGE_SIZE && (
          <div className="flex shrink-0 items-center justify-between gap-2 border-t pt-3">
            <Button type="button" variant="outline" size="sm" disabled={!canPrev} onClick={() => setOffset((o) => Math.max(0, o - EXPANSION_PRODUCTS_PAGE_SIZE))}>
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              {offset + 1}–{Math.min(offset + EXPANSION_PRODUCTS_PAGE_SIZE, total)} of {total}
            </span>
            <Button type="button" variant="outline" size="sm" disabled={!canNext} onClick={() => setOffset((o) => o + EXPANSION_PRODUCTS_PAGE_SIZE)}>
              Next
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
